import { ModelError_Item } from '@treadinc/horizon-api-spec';
import { t } from 'i18next';
import { useEffect } from 'react';

import Dialog from '~components/Dialog/Dialog';
import DialogActions from '~components/Dialog/DialogActions';
import DialogContent from '~components/Dialog/DialogContent';
import DialogHeader from '~components/Dialog/DialogHeader';
import { DriverForm, DriverFormValues } from '~components/DriverForm/DriverForm';
import {
  SmallButton,
  SmallLoadingButton,
} from '~components/Order/ordersDispatchStyledComponents';
import { InviteType } from '~components/UserInvite/UserInviteTypeSelector';
import { StagedComplianceDoc } from '~hooks/useComplianceDocuments/checklist';
import { useDriverForm } from '~hooks/useDriverForm/useDriverForm';

interface DriverCreateDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: DriverFormValues, inviteType: InviteType[]) => Promise<void>;
  isManagedDriver?: boolean;
  isLoading: boolean;
  asyncErrors?: ModelError_Item[];
  companyId?: string;
  managedCompanyId?: string;
  onStagedDocsChange?: (docs: StagedComplianceDoc[]) => void;
}

export const DriverCreateDialog = ({
  isOpen,
  onClose,
  onSubmit,
  isManagedDriver = false,
  isLoading,
  asyncErrors = [],
  companyId,
  managedCompanyId,
  onStagedDocsChange,
}: DriverCreateDialogProps) => {
  const {
    driverDialogRef,
    driverFormIsDirty,
    driverInviteType,
    driverInviteTypeError,
    setDriverInviteTypeError,
    onDriverFormStateChange,
    onDriverInviteTypeChange,
  } = useDriverForm();

  useEffect(() => {
    if (isOpen) {
      onDriverInviteTypeChange([]);
    }
  }, [isOpen, onDriverInviteTypeChange]);

  const handleSubmit = async () => {
    const isInviteTypeMissing = driverInviteType.length === 0;
    setDriverInviteTypeError(isInviteTypeMissing ? t('invite_type.required') : undefined);

    const driverData = await new Promise<DriverFormValues | undefined>((resolve) => {
      driverDialogRef.current?.submit((data) => resolve(data));
    });

    if (isInviteTypeMissing) {
      return;
    }

    if (driverData) {
      await onSubmit(driverData, driverInviteType);
    }
  };

  return (
    <Dialog isOpen={isOpen} onClose={onClose}>
      <DialogHeader onClose={onClose}>{t('user.create_driver')}</DialogHeader>

      <DialogContent sx={{ my: 4 }}>
        <DriverForm
          ref={driverDialogRef}
          onFormStateChange={onDriverFormStateChange}
          errors={asyncErrors}
          isManagedDriver={isManagedDriver}
          inviteTypes={driverInviteType}
          inviteTypeError={driverInviteTypeError}
          onInviteTypesChange={onDriverInviteTypeChange}
          isUpdating={isLoading}
          companyId={companyId}
          managedCompanyId={managedCompanyId}
          onStagedDocsChange={onStagedDocsChange}
        />
      </DialogContent>

      <DialogActions>
        <SmallButton
          color="secondary"
          disabled={isLoading}
          onClick={onClose}
          variant="outlined"
        >
          {t('actions.cancel')}
        </SmallButton>

        <SmallLoadingButton
          disabled={!driverFormIsDirty || isLoading}
          loading={isLoading}
          onClick={handleSubmit}
          variant="contained"
        >
          {t('actions.create')}
        </SmallLoadingButton>
      </DialogActions>
    </Dialog>
  );
};
